import { env } from "~/config/env";
import type { CMSMedia } from "~/types/cms";

export interface ResolvedMedia {
  url: string;
  width?: number;
  height?: number;
  alt?: string;
}

/**
 * Resolve media URL to full URL
 * Handles string URLs, CMSMedia objects, and simple media objects
 * If the URL is already absolute (starts with http), return it as-is
 * Otherwise, prepend the CMS API URL for relative paths
 * @param media - The media URL (string), CMSMedia object, simple media object, or number (unpopulated ID), can be undefined/null
 * @returns Full URL or null if no media provided or if unpopulated (number ID)
 */
export function resolveMediaUrl(
  media: string | number | CMSMedia | undefined | null
): string | null {
  if (!media) return null;

  // Handle number IDs (unpopulated relationships) - cannot resolve to URL
  if (typeof media === "number") return null;

  // Handle string URLs
  if (typeof media === "string") {
    // If URL is already absolute, use it directly
    if (media.startsWith("http")) return media;
    // Otherwise prepend CMS API URL
    return `${env.cmsApiUrl}${media}`;
  }

  // Handle CMSMedia objects
  const url = media.url;
  if (!url) return null;

  // If URL is already absolute, use it directly
  if (url.startsWith("http")) return url;
  // Otherwise prepend CMS API URL
  return `${env.cmsApiUrl}${url}`;
}

/**
 * Resolve the preferred Open Graph image for a media object
 * Prefers the CMS-generated `og` size variant (1200x630, the Open Graph standard)
 * and falls back to the original file with its real dimensions, so emitted
 * og:image:width/height always match the served image
 */
export function resolveOgImage(
  media: string | number | CMSMedia | undefined | null
): ResolvedMedia | null {
  if (!media || typeof media === "number" || typeof media === "string") {
    return resolveMedia(media);
  }

  const og = media.sizes?.og;
  if (og?.url) {
    return {
      url: og.url.startsWith("http") ? og.url : `${env.cmsApiUrl}${og.url}`,
      width: og.width ?? undefined,
      height: og.height ?? undefined,
      alt: media.alt || undefined,
    };
  }

  return resolveMedia(media);
}

/**
 * Resolve media object to get URL and dimensions
 * Handles string URLs, CMSMedia objects, simple media objects, and number IDs (unpopulated)
 */
export function resolveMedia(
  media: string | number | CMSMedia | undefined | null
): ResolvedMedia | null {
  if (!media) return null;

  // Handle number IDs (unpopulated relationships) - cannot resolve
  if (typeof media === "number") return null;

  let url: string | null = null;
  let width: number | undefined;
  let height: number | undefined;
  let alt: string | undefined;

  if (typeof media === "string") {
    url = media;
  } else {
    url = media.url;
    width = media.width;
    height = media.height;
    alt = media.alt || undefined;
  }

  if (!url) return null;

  if (!url.startsWith("http")) {
    url = `${env.cmsApiUrl}${url}`;
  }

  return { url, width, height, alt };
}

